import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
  title: string | null;
  phone: string | null;
  company: {
    name: string | null;
    duns_number: string | null;
    cage_code: string | null;
    business_type: string | null;
  } | null;
}

interface ProfileState {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: Partial<Omit<Profile, 'id' | 'email' | 'company'>>) => Promise<void>;
  updateCompanyInfo: (updates: Partial<NonNullable<Profile['company']>>) => Promise<void>;
}

export const useProfileStore = create<ProfileState>((set) => ({
  profile: null,
  loading: false,
  error: null,

  fetchProfile: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) throw new Error('No authenticated user');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          companies (
            name,
            duns_number,
            cage_code,
            business_type
          )
        `)
        .eq('id', session.user.id)
        .single();

      if (profileError) throw profileError;

      set({
        profile: {
          id: session.user.id,
          full_name: profile.full_name,
          avatar_url: profile.avatar_url,
          email: session.user.email,
          title: profile.title,
          phone: profile.phone,
          company: profile.companies,
        },
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      set({ error: 'Failed to load profile', loading: false });
    }
  },

  updateProfile: async (updates) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      toast.error('You must be logged in to update your profile');
      return;
    }

    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', session.user.id);

      if (error) throw error;

      set((state) => ({
        profile: state.profile ? { ...state.profile, ...updates } : null,
        loading: false,
      }));

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      set({ error: 'Failed to update profile', loading: false });
      toast.error('Failed to update profile');
    }
  },

  updateCompanyInfo: async (updates) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      toast.error('You must be logged in to update company information');
      return;
    }

    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('companies')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', session.user.id);

      if (error) throw error;

      set((state) => ({
        profile: state.profile ? {
          ...state.profile,
          company: {
            ...state.profile.company,
            ...updates,
          },
        } : null,
        loading: false,
      }));

      toast.success('Company information updated successfully');
    } catch (error) {
      console.error('Error updating company info:', error);
      set({ error: 'Failed to update company information', loading: false });
      toast.error('Failed to update company information');
    }
  },
}));